import * as React from 'react';
import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import useViewer from 'hooks/useViewer';
import type { GetWatchListQuery } from 'types/generated/client';
import {
  useGetWatchListQuery,
  useSolfDeleteWathclistItemMutation,
  useUpsertWatchListItemMutation,
} from 'types/generated/client';

type WatchListRows = GetWatchListQuery['watch_list'];

interface WatchlistContextValue {
  bookmarkedItems: Set<string>;
  handleBookmark: (symbol: string) => Promise<void>;
  isRemovingFromWatchlist: boolean;
  isUpdatingWatchlist: boolean;
  removeFromWatchlist: (id: string) => Promise<void>;
  /** Resolves true once the write is persisted, so callers can keep their input on failure. */
  setTickerWatching: (symbol: string, isWatching: boolean) => Promise<boolean>;
  watchListCalled: boolean;
  watchListData?: GetWatchListQuery;
  watchListError?: ApolloError;
  watchListLoading: boolean;
  watchListRefetch: () => Promise<ApolloQueryResult<GetWatchListQuery>>;
}

export const WatchlistContext = React.createContext<WatchlistContextValue | null>(null);

/** Every read and write compares tickers in one shape so a stored " aapl " still matches "AAPL". */
export const normalizeTicker = (ticker: string) => ticker.trim().toUpperCase();

const EMPTY_ROWS: WatchListRows = [];

const toWatchingSet = (rows: WatchListRows) =>
  new Set(
    rows
      .filter((row) => row.isWatching && !row.deletedAt)
      .map((row) => normalizeTicker(row.ticker))
      .filter(Boolean),
  );

const areSetsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value));

/** Owns the app-wide watchlist query so every feature observes the same state and mutations. */
export function WatchlistProvider({ children }: { children: React.ReactNode }) {
  const { viewer } = useViewer();
  const userId = viewer?.uid;
  const [bookmarkedItems, setBookmarkedItems] = React.useState<Set<string>>(new Set());
  /** Tickers with a write in flight, mapped to the state the user asked for. */
  const pendingTickers = React.useRef(new Map<string, boolean>());
  const [upsertWatchListItem, { loading: isUpdatingWatchlist }] = useUpsertWatchListItemMutation();
  const [softDeleteWatchListItem, { loading: isRemovingFromWatchlist }] =
    useSolfDeleteWathclistItemMutation();
  const {
    called: watchListCalled,
    data: watchListData,
    error: watchListError,
    loading: watchListLoading,
    refetch: watchListRefetch,
  } = useGetWatchListQuery({
    fetchPolicy: 'cache-and-network',
    skip: !userId,
    variables: { userId: userId || '' },
  });

  const rows = watchListData?.watch_list || EMPTY_ROWS;
  const hasForeignRows = rows.some((row) => row.userId !== userId);

  /**
   * The writers read through this ref so their identity never changes. They are handed to
   * memoized tables and column factories, which would otherwise rebuild across the whole app
   * every time the watchlist changes.
   */
  const latest = React.useRef({
    bookmarkedItems,
    rows,
    softDeleteWatchListItem,
    upsertWatchListItem,
    userId,
    watchListRefetch,
  });
  latest.current = {
    bookmarkedItems,
    rows,
    softDeleteWatchListItem,
    upsertWatchListItem,
    userId,
    watchListRefetch,
  };

  React.useEffect(() => {
    if (!userId || hasForeignRows) {
      pendingTickers.current.clear();
      setBookmarkedItems((current) => (current.size ? new Set() : current));
      return;
    }

    if (!watchListData?.watch_list) return;

    setBookmarkedItems((current) => {
      const next = toWatchingSet(watchListData.watch_list);
      // A snapshot taken before an in-flight write lands must not undo the optimistic toggle.
      pendingTickers.current.forEach((isWatching, ticker) => {
        if (isWatching) next.add(ticker);
        else next.delete(ticker);
      });
      // Apollo returns a new object on every broadcast, so only publish real changes. Consumers
      // re-filter and re-sort entire tables off this set.
      return areSetsEqual(current, next) ? current : next;
    });
  }, [hasForeignRows, userId, watchListData]);

  React.useEffect(() => {
    if (watchListError) console.error('Failed to load watchlist:', watchListError);
  }, [watchListError]);

  const setTickerWatching = React.useCallback(async (symbol: string, isWatching: boolean) => {
    const ticker = normalizeTicker(symbol);
    const currentUserId = latest.current.userId;
    if (!ticker || !currentUserId || pendingTickers.current.has(ticker)) return false;

    const wasWatching = latest.current.bookmarkedItems.has(ticker);
    pendingTickers.current.set(ticker, isWatching);
    setBookmarkedItems((current) => {
      const next = new Set(current);
      if (isWatching) next.add(ticker);
      else next.delete(ticker);
      return next;
    });

    // The upsert conflicts on the exact stored (userId, ticker), so a row saved as " AAPL " has
    // to be written back verbatim. Writing the normalized ticker would insert a second row and
    // leave the original watching, making the toggle look like it did nothing.
    const storedTickers = Array.from(
      new Set(
        latest.current.rows
          .filter((row) => !row.deletedAt && normalizeTicker(row.ticker) === ticker)
          .map((row) => row.ticker),
      ),
    );
    const targetTickers = storedTickers.length ? storedTickers : [ticker];

    try {
      await Promise.all(
        targetTickers.map((target) =>
          latest.current.upsertWatchListItem({
            variables: { isWatching, ticker: target, userId: currentUserId },
          }),
        ),
      );
    } catch (error) {
      console.error('Failed to update watchlist:', error);
      pendingTickers.current.delete(ticker);
      setBookmarkedItems((current) => {
        const next = new Set(current);
        if (wasWatching) next.add(ticker);
        else next.delete(ticker);
        return next;
      });
      return false;
    }

    try {
      await latest.current.watchListRefetch();
    } catch (error) {
      // The mutation succeeded, so retain the optimistic state until a later query reconciles it.
      console.error('Failed to refresh watchlist:', error);
    } finally {
      pendingTickers.current.delete(ticker);
    }

    return true;
  }, []);

  const handleBookmark = React.useCallback(
    async (symbol: string) => {
      await setTickerWatching(symbol, !latest.current.bookmarkedItems.has(normalizeTicker(symbol)));
    },
    [setTickerWatching],
  );

  const removeFromWatchlist = React.useCallback(async (id: string) => {
    if (!id) return;

    try {
      await latest.current.softDeleteWatchListItem({ variables: { id } });
    } catch (error) {
      console.error('Failed to remove watchlist item:', error);
      return;
    }

    try {
      await latest.current.watchListRefetch();
    } catch (error) {
      // The row is already soft deleted; the next query drops it from the list.
      console.error('Failed to refresh watchlist:', error);
    }
  }, []);

  const value = React.useMemo<WatchlistContextValue>(
    () => ({
      bookmarkedItems,
      handleBookmark,
      isRemovingFromWatchlist,
      isUpdatingWatchlist,
      removeFromWatchlist,
      setTickerWatching,
      watchListCalled,
      watchListData,
      watchListError,
      watchListLoading,
      watchListRefetch,
    }),
    [
      bookmarkedItems,
      handleBookmark,
      isRemovingFromWatchlist,
      isUpdatingWatchlist,
      removeFromWatchlist,
      setTickerWatching,
      watchListCalled,
      watchListData,
      watchListError,
      watchListLoading,
      watchListRefetch,
    ],
  );

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
}
